import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { searchApi } from "@/lib/entities";
import { DemoChip } from "@/components/Chips";
import { useSearchFocus } from "@/contexts/SearchFocusContext";

const GROUP_LABELS = {
  customers: "Customers",
  organizations: "Organizations",
  contacts: "Contacts",
  assets: "Fleet",
  drivers: "Drivers",
};

export const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const localRef = useRef(null);
  const { ref: sharedRef } = useSearchFocus();
  const wrapRef = useRef(null);
  const nav = useNavigate();

  // Attach the shared ref so app-level shortcuts can focus this input.
  const setInputRef = (el) => {
    localRef.current = el;
    if (sharedRef) sharedRef.current = el;
  };

  // Debounced fetch
  useEffect(() => {
    if (!q.trim()) {
      setResults(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchApi.global(q.trim());
        setResults(data);
        setOpen(true);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Esc while search is focused → close dropdown + blur
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        localRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // click outside → close
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (route) => {
    setOpen(false);
    setQ("");
    nav(route);
  };

  const totalCount =
    results ? Object.values(results).reduce((n, v) => n + (v?.length || 0), 0) : 0;

  return (
    <div className="relative w-full" ref={wrapRef}>
      <label className="relative block">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel" />
        <input
          ref={setInputRef}
          data-testid="global-search"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          placeholder="Search customers, coaches, drivers…"
          className="w-full bg-road-white border border-coach-silver rounded-sm py-1.5 pl-8 pr-16 text-[13px] text-carbon placeholder:text-steel focus:outline-none focus:ring-1 focus:ring-coach-blue"
        />
        {!q && (
          <span
            data-testid="global-search-hint"
            className="absolute right-2 top-1/2 -translate-y-1/2 hidden md:inline-flex items-center px-1.5 py-0.5 border border-coach-silver rounded-sm text-[10px] font-mono text-steel bg-white"
            aria-hidden="true"
          >
            /
          </span>
        )}
        {q && (
          <button
            data-testid="global-search-clear"
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-steel hover:text-carbon"
            onClick={() => {
              setQ("");
              setResults(null);
            }}
          >
            <X size={13} />
          </button>
        )}
      </label>

      {open && (
        <div
          data-testid="global-search-results"
          className="absolute mt-1 left-0 right-0 z-40 bg-white border border-coach-silver shadow-lg rounded-sm max-h-[70vh] overflow-y-auto"
        >
          {loading && (
            <div className="px-4 py-3 text-[12px] text-steel">Searching…</div>
          )}
          {!loading && totalCount === 0 && (
            <div data-testid="global-search-empty" className="px-4 py-3 text-[12px] text-steel">
              No results for &quot;{q}&quot;
            </div>
          )}
          {!loading &&
            results &&
            Object.entries(results).map(([group, rows]) => {
              if (!rows || rows.length === 0) return null;
              return (
                <div key={group} className="border-b border-coach-silver/60 last:border-b-0">
                  <div className="px-4 py-1.5 text-[10.5px] tracking-widest uppercase text-steel bg-road-white">
                    {GROUP_LABELS[group] || group}
                  </div>
                  {rows.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      data-testid={`global-search-result-${group}-${r.id}`}
                      onClick={() => go(r.route)}
                      className="w-full text-left px-4 py-2 hover:bg-road-white flex items-center gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] text-carbon truncate flex items-center gap-2">
                          {r.label} {r.is_demo && <DemoChip />}
                        </div>
                        {r.sublabel && (
                          <div className="text-[11px] text-steel truncate">{r.sublabel}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};
